import type { ReactElement } from 'react'

import type { ReadingScale } from './use-reading-scale.ts'

export interface ReadingScaleControlProps {
  readonly scale: ReadingScale
  readonly canIncrease: boolean
  readonly canDecrease: boolean
  readonly onIncrease: () => void
  readonly onDecrease: () => void
}

const GROUP_LABEL_ID = 'reading-scale-label'

/**
 * Pure presenter. Owns no state and runs no effects — the scale lives in
 * `useReadingScale`, the page wires the two together (Container / Presenter).
 *
 * Accessibility: a labelled group, 44x44 minimum pointer targets, and a polite
 * live region so screen-reader users hear the new size without a focus jump.
 *
 * THE READOUT IS NO LONGER DRAWN, AND THE LIVE REGION STAYS. "100%" between the
 * two steppers said, in a number, what the verse behind it says by being larger —
 * a sighted reader gets the answer from the page itself, and the pill was 4ch of
 * chrome above a poem to repeat it. A reader who cannot see the page has no such
 * second copy, so the `<output>` remains in the document and is clipped rather
 * than removed: press A+ and "125%" is still announced, politely, with no focus
 * jump. Delete it outright and the steppers become two buttons that answer
 * nothing.
 */
export function ReadingScaleControl({
  scale,
  canIncrease,
  canDecrease,
  onIncrease,
  onDecrease,
}: ReadingScaleControlProps): ReactElement {
  return (
    <div className="reading-scale" role="group" aria-labelledby={GROUP_LABEL_ID}>
      <span className="reading-scale__label" id={GROUP_LABEL_ID}>
        Tamanho do texto
      </span>

      <button
        type="button"
        className="reading-scale__button"
        onClick={onDecrease}
        disabled={!canDecrease}
        aria-label="Diminuir o tamanho do texto"
      >
        <span aria-hidden="true">A−</span>
      </button>

      <output className="reading-scale__status" aria-live="polite">
        {scale}%
      </output>

      <button
        type="button"
        className="reading-scale__button"
        onClick={onIncrease}
        disabled={!canIncrease}
        aria-label="Aumentar o tamanho do texto"
      >
        <span aria-hidden="true">A+</span>
      </button>
    </div>
  )
}
